import React, { useEffect, useState } from 'react';
import { Box, Container, Paper, Stack, Switch, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,  } from '@mui/material';
import { ArrowDropUp, ArrowDropDown } from '@mui/icons-material';
import { toast } from 'react-toastify';
import TooltipModal from './modals/tooltips/tooltipModal';
import DeleteTooltipModal from './modals/tooltips/deleteTooltipModal';
import ClassificationBannerHeader from '../common/banner/classifactionBannerHeader';
import PageHeader from '../common/banner/pageHeader';
import { sortAdminItems } from '../common/utils/tableUtil';
import SearchBar from '../common/search/SearchBar';

const TooltipsTable = ({ items, onToggle, sortConfig, onSortChange, fetchTooltips, setTooltips }) => {

  const handleUpdate = (tooltip) =>  {    
    toast.success(`${tooltip.page_name} Updated`, { autoClose: 3000 });
    fetchTooltips();
  };

  const handleDelete = (tooltip) =>  {
    setTooltips(items.filter(items => items.id !== tooltip.id));
    toast.success(`${tooltip.page_name} Deleted`, { autoClose: 3000 });
  };

  const handleSort = (key) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    onSortChange({ key, direction });
  };

  const SortIcon = ({ field }) => {
    if (sortConfig.key !== field) return null;
    return sortConfig.direction === 'asc' ? (
      <ArrowDropUp fontSize="small" />
    ) : (
      <ArrowDropDown fontSize="small" />
    );
  };

  return (
    <>
      <TableContainer component={Paper} sx={{ mt: 4 }}>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">    
          <TableHead>
            <TableRow sx={{ backgroundColor: '#0568AE' }}>
              {['Page', 'Description', 'Active'].map((field) => (
                <TableCell
                  key={field}
                  onClick={() => handleSort(field)}
                  sx={{ color: '#EEEEEE', fontWeight: 'bold', cursor: 'pointer', tooltipSelect: 'none' }}
                >
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <span>{field.charAt(0).toUpperCase() + field.slice(1)}</span>
                    <SortIcon field={field} />
                  </Stack>
                </TableCell>                
              ))}
              <TableCell sx={{ color: '#EEEEEE', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={item.id} sx={{ height: 56, backgroundColor: index % 2 === 0 ? '#2A2A2A' : '#000000' }}>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.page_name}</TableCell>
                <TableCell sx={{ color: '#FFFFFF' }}>{item.description}</TableCell>
                <TableCell> <Switch checked={!!item.active} onChange={(e) => onToggle(item, 'active', e.target.checked)} /> </TableCell>
                <TableCell>
                    <Stack direction="row" spacing={1}>
                        <TooltipModal tooltip={ item } onTriggerUpdate={handleUpdate} />
                        <DeleteTooltipModal tooltip={ item } onTriggerDelete={handleDelete} />
                    </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
}

export default function Tooltips() {
    const [tooltips, setTooltips] = useState([]);
    const [sortConfig, setSortConfig] = useState({ key: 'id', direction: 'asc' });
    const [searchQuery, setSearchQuery] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    useEffect(() => {
        fetchTooltips();
    }, []);

    const fetchTooltips = async () => {
        try {
                
            const response = await fetch(`${process.env.REACT_APP_API}/tooltips/`);            
            if (!response.ok) {
                toast.error(`HTTP error! status: ${response.status}`);
            } else {
                const json = await response.json();
                setTooltips(json.data);
            }
        } catch (error) {
            toast.error(error);
        } 
    };

    const handleCreate = (tooltip) =>  {
      toast.success(`${tooltip.page} Tooltip Created`, { autoClose: 3000 });
      fetchTooltips();
    };


    const handleUpdate = async (tooltip) => {
         const res = await fetch(`${process.env.REACT_APP_API}/tooltips/update`, {
             method: 'PUT',
             headers: { 'Content-Type': 'application/json' },
             body: JSON.stringify(tooltip)
         });
         if (!res.ok) {
            toast.error(`Failed to update tooltip : ${res.status}`);
          } else {
            setTooltips((prev) => prev.map((p) => (p.id === tooltip.id ? tooltip : p)));
         
            if ( tooltip.active ) {
              toast.success(`${tooltip.page} Tooltip Has Been Activated`, { autoClose: 3000 });
            } else {
              toast.success(`${tooltip.page} Tooltip Has Been De-Activated`, { autoClose: 3000 });
            }
            
          }
     };

    const handleSortChange = (newConfig) => {
        setSortConfig(newConfig);
    };

    const filtered = tooltips?.filter((item) => {
        const s = searchQuery.toLowerCase();
        return ['page', 'description'].some((field) => item[field]?.toLowerCase().includes(s));
    });

  const sorted = sortAdminItems(filtered, sortConfig);
  const paginated = sorted.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Container sx={{ minWidth:1800, mt: 10, mb: 10 }}>
        <Box sx={{pt: 2}}>
            <ClassificationBannerHeader />
            <PageHeader title="Tooltips" />
            <Stack direction="row" justifyContent="flex-end" mt={2} mb={1} sx={{ width: '100%', mx: 'auto' }}>                    
                  <SearchBar
                      value={searchQuery}
                      onChange={setSearchQuery}
                      placeholder="Search Tooltips..."
                      label="Search Tooltips..."
                      width={200}
                  />                
            </Stack>
          
            <TableContainer component={Paper} sx={{ mt: 4, backgroundColor: '#1A1A1A'  }}>
                <TooltipsTable
                    items={paginated}
                    onToggle={(item, key, value) => handleUpdate({ ...item, [key]: value })}
                    sortConfig={sortConfig}
                    onSortChange={handleSortChange}
                    fetchTooltips={fetchTooltips}
                    setTooltips={setTooltips}
                />
                <TablePagination
                    component="div"
                    count={sorted.length}
                    page={page}
                    onPageChange={(e, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
                    rowsPerPageOptions={[5, 10, 25, 50]}
                    sx={{ color: 'white', '& .MuiSvgIcon-root': { color: 'white' } }}
                />
            </TableContainer>
            <TooltipModal tooltip='' onTriggerCreate={handleCreate} />
        </Box>
    </Container>
  )
}
